import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, Surface, Text, useToast } from "@/shared/ui";
import { useShareConversation, useUnshareConversation } from "../hooks/useSharing";
import { CheckIcon, CopyIcon } from "./icons";

/** Public viewer URL for a share token (this app's own origin + `/share`). */
function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

export interface ShareDialogProps {
  open: boolean;
  conversationId: string;
  /** The conversation's current share token, if it has already been shared. */
  existingToken: string | null;
  onClose: () => void;
}

/**
 * Create + copy a public link to a conversation. Mirrors the ConfirmDialog
 * shell (portal · ink scrim · paper card). When the conversation is already
 * shared the link shows immediately; otherwise a single action mints it.
 */
export function ShareDialog({ open, conversationId, existingToken, onClose }: ShareDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const share = useShareConversation();
  const unshare = useUnshareConversation();
  const [token, setToken] = useState<string | null>(existingToken);
  const [copied, setCopied] = useState(false);

  // Re-sync local state whenever the dialog (re)opens or the conversation's
  // shared status changes underneath us.
  useEffect(() => {
    if (open) {
      setToken(existingToken);
      setCopied(false);
    }
  }, [open, existingToken]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function createLink() {
    share.mutate(conversationId, {
      onSuccess: (data) => setToken(data.share_token),
      onError: () => toast(t("share.error")),
    });
  }

  function copyLink() {
    if (!token) return;
    void navigator.clipboard
      .writeText(shareUrl(token))
      .then(() => {
        setCopied(true);
        toast(t("share.copied"));
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast(t("share.error")));
  }

  function stopSharing() {
    unshare.mutate(conversationId, {
      onSuccess: () => {
        setToken(null);
        setCopied(false);
        toast(t("share.unshared"));
      },
      onError: () => toast(t("share.unshareError")),
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("share.title")}
    >
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />
      <Surface
        tone="paper"
        radius="lg"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[460px] flex-col gap-3 p-6"
      >
        <Text variant="h4" as="h2">
          {t("share.title")}
        </Text>
        <Text variant="body" className="text-ink-soft">
          {t("share.description")}
        </Text>

        {token ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              readOnly
              value={shareUrl(token)}
              aria-label={t("share.linkLabel")}
              onFocus={(event) => event.currentTarget.select()}
              className="min-w-0 flex-1 rounded-sm bg-bubble px-3 py-2 font-sans text-[13px] text-ink-soft outline-none focus-visible:bg-tint"
            />
            <Button variant="ink" size="sm" onClick={copyLink} className="shrink-0">
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? t("share.copied") : t("share.copy")}
            </Button>
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-between gap-2">
          <div>
            {token ? (
              <Button
                variant="danger"
                size="sm"
                onClick={stopSharing}
                disabled={unshare.isPending}
              >
                {unshare.isPending ? t("share.unsharing") : t("share.unshare")}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t("common.close")}
            </Button>
            {!token ? (
              <Button variant="ink" size="sm" onClick={createLink} disabled={share.isPending}>
                {share.isPending ? t("share.creating") : t("share.create")}
              </Button>
            ) : null}
          </div>
        </div>
      </Surface>
    </div>,
    document.body,
  );
}
